import React from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route } from "react-router-dom";
import AuthSignInForm from "../modules/auth/AuthSignInForm";
import AuthSignUpForm from "../modules/auth/AuthSignUpForm";
import ForgotPasswordForm from "../modules/auth/ForgotPasswordForm";
import NewPasswordForm from "../modules/auth/NewPasswordForm";
import SuccessResetPasswordMsg from "../modules/auth/SuccessResetPasswordMsg";
import { TableNames } from "../modules/tables/components/utils";
import AboutUsPage from "../pages/AboutUsPage";
import ContactUsPage from "../pages/ContactUsPage";
import ProfilePage from "../pages/ProfilePage";
import TermsOfServicePage from "../pages/TermsOfServicePage";

import {
    AboutUsPath, ContactUsPath,
    forgotPasswordPath,
    loginPath, MyProfilePath, newPasswordFormPath,
    notFound, ProjectRelativePath,
    registrationPath,
    SettingsPath,
    successRegistrationPath, successResetPasswordPath, TermsOfServicePath,
} from "./paths";
import { observer } from "mobx-react-lite";
import SettingsPage from "../pages/SettingsPage";

import SignInPage from "../pages/SignInPage";

import ErrorPage from "../pages/ErrorPage";
import App from "../App";
import SuccessSignUpMessagePage from "../pages/SuccessSignUpMessagePage";
import authUserStore from "../modules/auth/store";
import ProjectsPage from "../modules/project/components/ProjectsPage";
import NodeTable from "../modules/tables/components/nodesTable/NodeTable";
import ProjectMainTab from "../modules/project/components/ProjectMainTab";
import CalculationsPage from "../modules/CalculationsPage/CalculationsPage";
import EquipmentsTable from "../modules/tables/components/equipmentsTable/EquipmentsTable";
import BranchesTable from "../modules/tables/components/branchesTable/BranchesTable";

/**
 * Обертка для приватных роутов, доступ к которым должен быть только о авторизованных пользователей.
 *
 * Проверяет наличие объекта user в `localStorage`, если объект есть, то пользователь считается авторизованным.
 * Если объекта нет, то происходит редирект на страницу логина.
 * */
const ProtectedLayout = observer(() => {
    if (!authUserStore.isLogin) {
        return <Navigate to={loginPath} />;
    }
    return <Outlet />;
});

/**
 * Обертка для публичных роутов, доступ к которым должен быть только у неавторизованных пользователей.
 *
 * Проверяет наличие объекта user в `localStorage`, если объект есть, то пользователь считается авторизованным.
 * Авторизованные пользователи редиректятся к данным (приватным роутам).
 * */
const PublicLayout = observer(() => {
    if (authUserStore.isLogin) {
        return <Navigate to={ProjectRelativePath} />;
    }
    return <Outlet />;
});

/**
 * Обертка предоставляющая доступ к текущему авторизованному пользователю `user` и методам `login`/`logout`.
 * */
const AuthLayout = () => {
    return <Outlet />;
};


// Для использования нового api react-router-dom нужно определние с помощью `createRoutesFromElements`
export const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route path="/" element={<App />} errorElement={<ErrorPage />}>
                <Route element={<AuthLayout />}>
                    <Route element={<PublicLayout />}>
                        <Route index element={<Navigate to={loginPath} replace={true} />} />

                        <Route element={<SignInPage />}>
                            <Route path={loginPath} element={<AuthSignInForm />} />
                            <Route path={registrationPath} element={<AuthSignUpForm />} />
                            <Route path={forgotPasswordPath} element={<ForgotPasswordForm />} />
                        </Route>

                        <Route path={successRegistrationPath} element={<SuccessSignUpMessagePage />} />
                        <Route path={successResetPasswordPath} element={<SuccessResetPasswordMsg />} />
                        <Route path={newPasswordFormPath} element={<NewPasswordForm />} />

                        <Route path={notFound} element={<Navigate to={loginPath} replace={true} />} />
                    </Route>

                    <Route element={<ProtectedLayout />}>
                        <Route path="/" element={<Outlet />} errorElement={<ErrorPage />}>

                            <Route path={MyProfilePath} element={<ProfilePage />} />
                            <Route path={ProjectRelativePath} element={<ProjectsPage />} />
                            <Route path={`${ProjectRelativePath}:projectId/`} element={<Outlet />}>
                                <Route index element={<Navigate to={"nodes"} replace={true} />} />

                                <Route path={`nodes/`} element={<NodeTable tableName={TableNames.NODES} />} />
                                <Route path={`equipments/`} element={<EquipmentsTable tableName={TableNames.EQUIPMENTS} />} />
                                <Route path={`branches/`} element={<BranchesTable tableName={TableNames.BRANCHES} />} />
                                <Route path={`calculations/`} element={<CalculationsPage />} />

                                <Route path={`settings/`} element={<ProjectMainTab />} />
                            </Route>

                            {/* ==================== settings routes ==================== */}
                            <Route path={SettingsPath} element={<SettingsPage />} />
                        </Route>
                    </Route>

                    <Route path={AboutUsPath} element={<AboutUsPage />} />
                    <Route path={ContactUsPath} element={<ContactUsPage />} />
                    <Route path={TermsOfServicePath} element={<TermsOfServicePage />} />
                </Route>
            </Route>
        </>
    )
);
